import 'react-datepicker/dist/react-datepicker.css';
import 'react-select/dist/react-select.css';
import * as classes from './customer-detail.scss';

import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { Control, Errors, LocalForm } from 'react-redux-form';
import ReactTable from 'react-table';
import { Column } from 'react-table';

import { CustomerViewModel, CustomerContractModel } from '@models/Customer';
import { ContractSimpleViewModel } from '@models/Contract';
import { CommentType } from '@models/Comment';
import { CommentSearch } from '../../../comments';
import { FileList, CreditCardList } from '@common/components';
import { dateParser } from '@common/dateParser';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { FileViewModel } from '@models/Document';
import { Breadcrumbs, SpinnerPage, Tabs, Tab } from '@common/components';
import { AccountType } from '@models/Account';
import { Role, Roles } from '@models/Roles';
import { hasValue, getDefaultErrorMessages } from '@common/validators';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface CustomerDetailsComponentProperties extends RouteComponentProps<{ id: number }> {
  readonly message: string;
  readonly getCustomer: (id: number) => void;
  readonly customerVM: CustomerViewModel;
  readonly getContracts: (id: number, pageNumber: number, pageSize: number) => void;
  readonly contractsLoading: boolean;
  readonly contractsPageCount: number;
  readonly contractsPageNumber: number;
  readonly contractsPageSize: number;
  readonly contractsResults: ContractSimpleViewModel[];
  readonly getRelatedContracts: (id: number, pageNumber: number, pageSize: number) => void;
  readonly relatedContractsLoading: boolean;
  readonly relatedContractsPageCount: number;
  readonly relatedContractsPageNumber: number;
  readonly relatedContractsPageSize: number;
  readonly relatedContractsResults: ContractSimpleViewModel[];
  readonly uploadFiles: (customerId: number, fileVMs: FileViewModel[]) => void;
  readonly removeFile: (customerId: number, fileId: number) => void;
  readonly resetPassword: (applicationId: number) => void;
  readonly onSaveClick: (customerContract: CustomerContractModel) => void;
  readonly removeRelated: (customerContract: CustomerContractModel) => void;
}

// Component definition
export class CustomerDetailsComponent extends React.Component<
  CustomerDetailsComponentProperties,
  { withHostedCheckout: boolean }
> {
  readonly withHostedCheckout: boolean = true;
  constructor(props: CustomerDetailsComponentProperties) {
    super(props);

    this.onUploadClick = this.onUploadClick.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
  }

  componentDidMount() {
    if (this.props.match && this.props.match.params.id) {
      this.props.getCustomer(this.props.match.params.id);
    }
  }

  onUploadClick(fileVMs: FileViewModel[]) {
    this.props.uploadFiles(this.props.customerVM.id, fileVMs);
  }

  onRemoveClick(fileId: number) {
    this.props.removeFile(this.props.customerVM.id, fileId);
  }

  onPasswordResetClick(accountId?: number) {
    if (accountId) {
      this.props.resetPassword(accountId);
    }
  }
  handleSubmit = (formValues: { contractNum: string; relationship: string }) => {
    const cc: CustomerContractModel = {
      customerId: this.props.customerVM.id,
      contractNum: formValues.contractNum,
      relationship: formValues.relationship
    };
    this.props.onSaveClick(cc);
  };

  handleRemove = (vm: ContractSimpleViewModel) => {
    const cc: CustomerContractModel = {
      customerId: this.props.customerVM.id,
      contractNum: vm.number,
      relationship: ''
    };
    this.props.removeRelated(cc);
  };

  renderError() {
    if (!this.props.message) {
      return <div />;
    }
    return <div className="error">{this.props.message}</div>;
  }

  render() {
    if (!this.props.customerVM) {
      return <SpinnerPage />;
    }

    const columns: Column[] = [
      {
        accessor: (contract: ContractSimpleViewModel) =>
          contract && (
            <Link
              to={{
                pathname: getRoute(RouteDefinitions.contract.Details, {
                  id: contract.id
                }),
                state: { viewOnly: true }
              }}
            >
              {contract.number}
            </Link>
          ),
        id: 'ContractNumber',
        Header: 'Contract Number',
        sortable: false
      },
      {
        accessor: (d: ContractSimpleViewModel) =>
          d.propertyAddress
            ? d.propertyAddress.line1 +
              ', ' +
              d.propertyAddress.line2 +
              ', ' +
              d.propertyAddress.city +
              ', ' +
              d.propertyAddress.state.abbreviation +
              '  ' +
              d.propertyAddress.zip
            : '',
        id: 'propertyAddress',
        Header: 'Address',
        sortable: false
      },
      {
        accessor: (d: ContractSimpleViewModel) => (d.contractStatus ? d.contractStatus.value : ''),
        id: 'contractStatus',
        Header: 'Status',
        sortable: false
      },
      {
        accessor: (d: ContractSimpleViewModel) =>
          d.expirationDate ? dateParser.parse(d.expirationDate).format('MM/DD/YYYY') : '',
        id: 'expirationDate',
        Header: 'Expiration',
        sortable: false
      }
    ];

    const relatedColumns: Column[] = [
      {
        accessor: (contract: ContractSimpleViewModel) =>
          contract && (
            <Link
              to={{
                pathname: getRoute(RouteDefinitions.contract.Details, {
                  id: contract.id
                }),
                state: { viewOnly: true }
              }}
            >
              {contract.number}
            </Link>
          ),
        id: 'ContractNumber',
        Header: 'Contract #',
        sortable: false
      },
      {
        accessor: (d: ContractSimpleViewModel) => (d.contractStatus ? d.contractStatus.value : ''),
        id: 'contractStatus',
        Header: 'Contract Status',
        sortable: false
      },
      {
        accessor: (d: ContractSimpleViewModel) => (d.relationship ? d.relationship : 'N/A'),
        id: 'relationship',
        Header: 'Relationship to Contract',
        sortable: false
      },
      {
        Header: 'Remove',
        id: 'remove',
        maxWidth: 75,
        sortable: false,
        accessor: (d: ContractSimpleViewModel) => {
          return (
            <i
              className="fas fa-times red-text pointer"
              onClick={() => {
                this.handleRemove(d);
              }}
            />
          );
        }
      }
    ];

    var customer = this.props.customerVM;
    return (
      <>
        <Breadcrumbs />
        <div className={classes.customerDetail}>
          <div className="row">
            <div className="col-md-8">
              <h2>
                Customer #{customer.id}
                {Role.CheckRoles(
                  [Roles.Admin, ...Role.GetAllIBS(), ...Role.GetAllService(), ...Role.GetAllAccounting()]
                ) && (
                  <>
                    <span className="ml-10 pl-10">
                      <Link
                        to={getRoute(RouteDefinitions.customer.Edit, {
                          id: customer.id
                        })}
                      >
                        Edit
                      </Link>
                    </span>
                    {customer.isOlo && (
                      <span
                        className="ml-10 pl-10 red-text pointer"
                        onClick={() => this.onPasswordResetClick(customer.accountId)}
                      >
                        Reset Web Password
                      </span>
                    )}
                  </>
                )}
              </h2>
              <div className={classes.contactContainer}>
                <div className="row p-10">
                  <div className="col-md-7">
                    <div className="row">
                      <div className="col-md-6">
                        <label>Name: </label>
                        <span>{customer.name}</span>
                      </div>
                      <div className="col-md-6">
                        <label>Email Address: </label>
                        <span className="ellipsis">{customer.emailAddress}</span>
                      </div>
                    </div>
                    <div className="row mt-25">
                      <div className="col-md-6">
                        <label>Customer Type: </label>
                        <span>{customer.customerType ? customer.customerType.value : '(--)'}</span>
                      </div>
                      <div className="col-md-6">
                        <label>Customer Status: </label>
                        <span>{customer.customerStatus ? customer.customerStatus.value : '(--)'}</span>
                      </div>
                      {customer.company &&
                        customer.company.id > 0 && (
                          <div className="col-md-6">
                            <label>Customer Company: </label>
                            <span>{customer.company.value}</span>
                          </div>
                        )}
                    </div>
                    <div className="row mt-25">
                      <div className="col-md-6">
                        <label>Primary Phone: </label>
                        <span>
                          {customer.phone}{' '}
                          {customer.phone && customer.phone1Type && customer.phone1Type.id > 0
                            ? customer.phone1Type.value
                            : '(--)'}
                        </span>
                      </div>
                      <div className="col-md-6">
                        <label>Secondary Phone: </label>
                        <span>
                          {customer.phone2}{' '}
                          {customer.phone2 && customer.phone2Type && customer.phone2Type.id > 0
                            ? customer.phone2Type.value
                            : '(--)'}
                        </span>
                      </div>
                    </div>
                    <div className="row mt-25">
                      <div className="col-md-6">
                        <label>Language: </label>
                        <span>{customer.language ? customer.language.value : ''}</span>
                      </div>
                      <div className="col-md-6">
                        <label>Contact Preference: </label>
                        <span className="p-10">
                          USPS <i className={customer.usps ? 'fa fa-check' : 'fa fa-times'} />
                        </span>
                        <span className="p-10">
                          Email <i className={customer.email ? 'fa fa-check' : 'fa fa-times'} />
                        </span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        {customer.accountId && (
                          <CreditCardList
                            accountId={customer.accountId}
                            accountType={AccountType.Customer}
                            withHostedCheckout={true}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div>
                      <label>Addresses:</label>
                      <div className="col-md-12 vertical-scroll h-150">
                        {customer.addresses &&
                          customer.addresses.length > 0 &&
                          customer.addresses.map((address, index) => {
                            return (
                              <div key={index} className="row m-0 mt-5 dark-gray-bc">
                                <div className="col-md-12">
                                  <div className="row">
                                    <div className="col-md-6">
                                      <span>
                                        {address.line1}
                                        {address.line2 ? ', ' + address.line2 : ''}
                                      </span>
                                    </div>
                                    <div className="col-md-6">
                                      <label>International Address: </label>
                                      <span>{address.international ? 'Yes' : 'No'}</span>
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-md-6">
                                      <span>
                                        {address.city}
                                        {address.state ? ', ' + address.state.abbreviation : ''}
                                        {'  ' + address.zip}
                                      </span>
                                    </div>
                                    <div className="col-md-6">
                                      <label>Mailing Address: </label>
                                      <span>{address.primary ? 'Yes' : 'No'}</span>
                                    </div>
                                  </div>
                                  {address.international ? (
                                    <div className="row">
                                      <div className="col-md-6">
                                        <span>{address.country}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                  <br />
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <FileList
                fileList={customer.documents}
                uploadCallback={this.onUploadClick}
                removeCallback={this.onRemoveClick}
                uploadModalTitle={'Upload Files for Customer: ' + customer.name}
                removeModalTitle={'Remove File for Customer: ' + customer.name}
                forContract={false}
                canRemove={Role.CheckRoles([Roles.Admin, Roles.IBSAdmin, Roles.IBSManager])}
              />
            </div>
          </div>
          <div className="row mt-10 mb-10 pt-10 pb-10">
            <Tabs>
              <Tab title={'Contracts'}>
                <h3>Contracts</h3>
                <div className="col-md-12">
                  <ReactTable
                    onPageChange={() => {
                      Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                        element.scrollTop = 0;
                      });
                    }}
                    className="-striped -highlight"
                    columns={columns}
                    data={this.props.contractsResults}
                    defaultPageSize={10}
                    loading={this.props.contractsLoading}
                    manual={true}
                    onFetchData={this.handleFetchData}
                    pages={this.props.contractsPageCount}
                  />
                </div>
              </Tab>
              <Tab title={'Related Contracts'}>
                <h3>Related Contracts</h3>
                <div className="row">
                  <div className="col-md-12">
                    <LocalForm hideNativeErrors={true} onSubmit={this.handleSubmit}>
                      <div className="row text-left">
                        <div className="col-md-3">
                          <label htmlFor=".contractNum">Associate Contract Number</label>
                          <Control.text model=".contractNum" validators={{ required: hasValue }} />
                          <Errors
                            className="error"
                            model=".contractNum"
                            show={{ touched: true, focus: false }}
                            messages={getDefaultErrorMessages()}
                          />
                        </div>
                        <div className="col-md-3">
                          <label htmlFor=".relationship">Relationship</label>
                          <Control.text model=".relationship" validators={{ required: hasValue }} />
                          <Errors
                            className="error"
                            model="..relationship"
                            show={{ touched: true, focus: false }}
                            messages={getDefaultErrorMessages()}
                          />
                        </div>
                        <div className="row bottom-xs">
                          <div className="col-md-3">
                            <button className="btn btn-primary" type="submit">
                              Associate Contract
                            </button>
                          </div>
                        </div>
                      </div>
                    </LocalForm>
                  </div>
                </div>
                <div className="col-md-12">
                  <ReactTable
                    onPageChange={() => {
                      Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                        element.scrollTop = 0;
                      });
                    }}
                    className="-striped -highlight"
                    columns={relatedColumns}
                    data={this.props.relatedContractsResults}
                    defaultPageSize={10}
                    loading={this.props.relatedContractsLoading}
                    manual={true}
                    onFetchData={this.handleRelatedFetchData}
                    pages={this.props.relatedContractsPageCount}
                  />
                </div>
              </Tab>
            </Tabs>
          </div>
          <CommentSearch entityId={this.props.match.params.id} commentType={CommentType.Customer} />
        </div>
      </>
    );
  }

  private handleFetchData = (state: { page: number; pageSize: number }): void => {
    this.props.getContracts(
      this.props.match.params.id ? this.props.match.params.id : 0,
      state.page + 1,
      state.pageSize
    );
  };

  private handleRelatedFetchData = (state: { page: number; pageSize: number }): void => {
    this.props.getRelatedContracts(
      this.props.match.params.id ? this.props.match.params.id : 0,
      state.page + 1,
      state.pageSize
    );
  };
}



// WEBPACK FOOTER //
// ./src/customer/_components/customer-details/customer-details.component.tsx